import { VStack, useDisclosure } from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { Table, LearnMoreDrawer, MetricLearnMoreHeader } from 'Molecules';
import { getMetricRefNumber } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/Metrics.hooks';
import {
  MaterialityState,
  MetricMaterialityState,
} from '../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';
import {
  checkMateriality,
  getMetricMaterialityForStandardBrowser,
} from '../DoubleMaterialityAssessment/DrMateriality/DrMateriality.hooks';
import {
  COLLECT_ONLY_OPTIONS,
  METRIC_MATERIALITY_OPTIONS,
  VSME_METRIC_MATERIALITY_OPTIONS,
} from '../DoubleMaterialityAssessment/DrMateriality/DrMaterialityUtils';
import { TableFilters } from './BrowserTableComponents/TableFilters';
import { useTableColumns } from './BrowserTableComponents/useTableColumns';
import { useTableFilters } from './BrowserTableComponents/useTableFilters';
import {
  GetMetricsForMfDrDocument_,
  Esrs_MaterialStandard_Constraint_,
  Esrs_MaterialStandard_Update_Column_,
  GetMFdrForMFstandardDocument_,
  GetMFdrForMFstandardQuery_,
  GetParentAssessmentMaterialMetricsQuery_,
  GetSelectedDataPointsDocument_,
  QuestionType_Enum_,
  useDeleteMaterialMetricMutation,
  useUpsertDrMaterialMetricsMutation,
  GetRequiredBuMaterialMetricsDocument_,
  EsrsAssessmentDocument_,
  GetOptimizedMetricsDrDocument_,
  GetRequiredMaterialMetricsDocument_,
  MaterialMetricsPerDisclosureDocument_,
  ReportingUnitsMetricsPerDisclosureDocument_,
  GetParentMaterialityAssessmentQuery_,
} from 'models';
import { FrameworkRefs, Frameworks } from '../../Assessment';
import { useParams } from 'react-router-dom';
import { DataCollectionLevel } from '../../DataCollection';
import { useCompanyType, useToast } from 'utils/hooks';
import { SearchEmptyState } from 'Atoms';
import { uniq } from 'lodash';

type Metric =
  GetMFdrForMFstandardQuery_['MFStandard'][0]['mfDisclosureRequirementGroups'][0]['mfDisclosureRequirements'][0]['mfDisclosureRequirementItems'][0]['metric'];

export type mfDisclosures = {
  title: string;
  type: QuestionType_Enum_ | null | undefined;
  framework: FrameworkRefs;
  reference: string;
  subCategory: string;
  description?: string | null;
  metric: Metric;
  materialStandardId: string;
}[];

type MFStandardBrowserTableProps = {
  mfDisclosures: mfDisclosures;
  isCollectOnly: boolean;
  parentMaterialMetrics:
    | GetParentAssessmentMaterialMetricsQuery_['esrs_MaterialMetric']
    | undefined;
  hasPhasedInOption: boolean;
  isNotAssessedOrNotMaterial: boolean;
  isDrView: boolean;
  parentMaterialStandards?: NonNullable<
    NonNullable<GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']>['parentAssessment']
  >['materialStandards'];
};

type PartialMetricsData = Omit<Metric, 'childrenMetrics'>;

export interface MaybeHasChild extends PartialMetricsData {
  childrenMetrics?: { childMetric: MaybeHasChild }[];
}

export const getAllChildrenReferences = (metric: Metric): string[] => {
  const childrenRefs: string[] = [];

  const recurseChildren = (currentMetric: MaybeHasChild) => {
    childrenRefs.push(currentMetric.reference);
    if (!currentMetric.childrenMetrics?.length) {
      return;
    }
    currentMetric.childrenMetrics?.forEach(({ childMetric }) => {
      recurseChildren(childMetric);
    });
  };
  if (metric.childrenMetrics?.length) {
    metric.childrenMetrics.forEach((child) =>
      !!child.childMetric ? recurseChildren(child.childMetric as unknown as MaybeHasChild) : {}
    );
  }
  return uniq(childrenRefs);
};

export const MFStandardBrowserTable = ({
  mfDisclosures,
  isCollectOnly,
  parentMaterialMetrics,
  hasPhasedInOption,
  isNotAssessedOrNotMaterial,
  isDrView,
  parentMaterialStandards,
}: MFStandardBrowserTableProps) => {
  const { framework = '', esrsAssessmentId } = useParams();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedMetric, setSelectedMetric] = useState<Metric>();
  const [upsertMaterialMetrics] = useUpsertDrMaterialMetricsMutation();
  const [deleteMaterialMetric] = useDeleteMaterialMetricMutation();
  const { isGroup } = useCompanyType();
  const toast = useToast();

  const selectOptions = useMemo(() => {
    if (isCollectOnly) {
      return COLLECT_ONLY_OPTIONS;
    }

    return (
      framework === FrameworkRefs.vsme
        ? VSME_METRIC_MATERIALITY_OPTIONS
        : METRIC_MATERIALITY_OPTIONS
    ).filter((opt) => (!hasPhasedInOption ? opt.value !== MetricMaterialityState.phasedIn : true));
  }, [isCollectOnly, hasPhasedInOption]);

  const filters = useTableFilters(mfDisclosures);

  const handleUpdateMateriality = (data: {
    selection: MetricMaterialityState;
    metric: Metric;
    materialStandardId?: string;
  }) => {
    const { selection, metric, materialStandardId } = data;
    const childrenMetrics = getAllChildrenReferences(metric);

    const isMaterial = checkMateriality(selection, isCollectOnly);
    const isDataGatheringOnly =
      selection === MetricMaterialityState.gatherData ||
      selection === MetricMaterialityState.phasedIn;
    const isPhasedIn = selection === MetricMaterialityState.phasedIn;
    const metricMaterialStandardId = metric.materialMetrics?.[0]?.materialStandardId;
    const dataCollection = isGroup ? DataCollectionLevel.subsidiaries : DataCollectionLevel.company;

    const isMetricNotInFramework = metric.mfDisclosureRequirements_aggregate.aggregate?.count === 0;

    const materialStandard = isMetricNotInFramework
      ? {
          materialStandard: {
            data: {
              standardRef:
                metric.mfDisclosureRequirements?.[0]?.mfDisclosureRequirement
                  .mfDisclosureRequirementGroup.mfStandardRef,
              assessmentId: esrsAssessmentId,
              isMaterial: true,
            },
            on_conflict: {
              constraint:
                Esrs_MaterialStandard_Constraint_.MaterialStandardStandardRefAssessmentIdKey_,
              update_columns: [Esrs_MaterialStandard_Update_Column_.IsMaterial_],
            },
          },
        }
      : {
          materialStandardId: metricMaterialStandardId ?? materialStandardId,
        };

    const metricData = {
      ...materialStandard,
      metricRef: metric.reference,
      isMaterial,
      isDataGatheringOnly,
      isPhasedIn,
      dataCollection,
    };

    const chidrenData =
      childrenMetrics.map((childRef) => ({
        ...materialStandard,
        metricRef: childRef,
        isMaterial,
        isDataGatheringOnly,
        isPhasedIn,
        dataCollection,
      })) ?? [];
    toast({
      text: 'Updating materiality',
      variant: 'default',
    });
    upsertMaterialMetrics({
      variables: {
        objects: [metricData, ...chidrenData],
      },
      onCompleted: () => {
        toast({
          text: 'Materiality updated!',
          variant: 'default',
        });
      },
      onError: () => {
        toast({
          text: 'Failed to update materiality',
          variant: 'danger',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        GetMFdrForMFstandardDocument_,
        GetSelectedDataPointsDocument_,
        GetMetricsForMfDrDocument_,
        GetOptimizedMetricsDrDocument_,
        ReportingUnitsMetricsPerDisclosureDocument_,
        GetRequiredMaterialMetricsDocument_,
        GetRequiredBuMaterialMetricsDocument_,
        MaterialMetricsPerDisclosureDocument_,
        EsrsAssessmentDocument_,
      ],
    });
  };

  const handleDeleteMetric = (metric: Metric) => {
    const materialMetric = metric.materialMetrics?.[0];

    if (!!materialMetric) {
      deleteMaterialMetric({
        variables: { id: materialMetric?.id },
        refetchQueries: [
          GetMFdrForMFstandardDocument_,
          GetSelectedDataPointsDocument_,
          GetMetricsForMfDrDocument_,
          GetOptimizedMetricsDrDocument_,
          ReportingUnitsMetricsPerDisclosureDocument_,
          GetRequiredMaterialMetricsDocument_,
          GetRequiredBuMaterialMetricsDocument_,
          MaterialMetricsPerDisclosureDocument_,
          EsrsAssessmentDocument_,
        ],
      });
    }
  };
  const getMateriality = (metric: Metric) => {
    return getMetricMaterialityForStandardBrowser(
      metric,
      isCollectOnly,
      framework,
      parentMaterialMetrics
    );
  };

  const getReference = (metric: Metric) => {
    return `${metric.disclosureRequirementRef} ${getMetricRefNumber(metric)}`;
  };

  const isDataCollectionGroup = (
    metric: Metric,
    parentMetrics: GetParentAssessmentMaterialMetricsQuery_['esrs_MaterialMetric'] | undefined
  ): boolean => {
    if (!metric.childrenMetrics?.length) {
      const parentMaterialMetric = parentMetrics?.find((met) => met.metricRef === metric.reference);

      return parentMaterialMetric?.dataCollection === DataCollectionLevel.group;
    }
    return (
      metric.childrenMetrics.every((met) => {
        return met.childMetric && isDataCollectionGroup(met.childMetric as Metric, parentMetrics);
      }) ?? false
    );
  };

  const getParentMetricMaterialityAndDataCollection = (metric: Metric) => {
    const parentMaterialMetric = parentMaterialMetrics?.find(
      (met) => met.metricRef === metric.reference
    );

    const metricDr = parentMaterialMetric?.metric?.requirement?.reference;
    const metricStandard = parentMaterialMetric?.materialStandard?.standardRef;
    const parentDrMaterialMetrics = parentMaterialMetrics?.filter(
      (met) => met.metric.requirement.reference === metricDr
    );
    const isParentStandardMaterial = parentMaterialStandards?.find(
      (parentStandard) => parentStandard.standardRef === metricStandard
    )?.isMaterial;

    const parentDrMateriality =
      !isParentStandardMaterial || parentDrMaterialMetrics?.every((met) => met.isMaterial === false)
        ? MaterialityState.notMaterial
        : '';

    const isParentMetricMaterial =
      parentMaterialMetric?.isMaterial && parentDrMateriality !== MaterialityState.notMaterial;

    const isParentMetricGroupLevel = isDataCollectionGroup(metric, parentMaterialMetrics);

    return { isParentMetricMaterial, isParentMetricGroupLevel };
  };

  const columns = useTableColumns({
    framework,
    selectOptions,
    onUpdateMateriality: handleUpdateMateriality,
    onDeleteMetric: handleDeleteMetric,
    onOpenLearnMore: (metric) => {
      setSelectedMetric(metric);
      onOpen();
    },
    getMateriality,
    getReference,
    isNotAssessedOrNotMaterial,
    isDrView: isDrView,
    getParentMetricMaterialityAndDataCollection,
  });

  const getSelectedMetricTags = (metric?: Metric) => {
    const materialMetric = metric?.materialMetrics?.[0];
    const tags = !!materialMetric?.materialMetricTags?.length
      ? materialMetric?.materialMetricTags?.map((tag) => ({
          type: tag.type,
        }))
      : metric?.tags.map((t) => ({ type: t.type }));
    return tags ?? [];
  };

  return (
    <VStack gap="16px" alignItems="start" width={'100%'}>
      <TableFilters {...filters} isDrView={isDrView} />
      {filters.filteredData.length > 0 ? (
        <Table
          data={filters.filteredData}
          columns={columns}
          cellProps={{ padding: '2px 8px 2px 8px' }}
          headerPadding="2px 8px 2px 8px"
          conditionalRowProps={(row) =>
            row.metric.requirement.frameworkRef === framework ? { background: 'bg.disabled' } : {}
          }
        />
      ) : (
        <SearchEmptyState setSearch={filters.setSearchValue} />
      )}
      <LearnMoreDrawer
        isOpen={isOpen}
        onClose={onClose}
        description={selectedMetric?.description ?? ''}
        header={selectedMetric?.requirement?.title ?? ''}
        customHeader={
          selectedMetric ? (
            <MetricLearnMoreHeader
              metricRef={selectedMetric?.reference ?? ''}
              tags={getSelectedMetricTags(selectedMetric)}
              frameworkName={
                Frameworks[selectedMetric?.requirement?.frameworkRef as keyof typeof FrameworkRefs]
              }
            />
          ) : undefined
        }
      />
    </VStack>
  );
};
